import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { getSelectedLanguage, useLongFormAiListener, useProductOfferStore } from "@expert/sales-tools";

import { getSessionByTaskId } from "@/sdk/sessions/getters";

/**
 * Mounts the inbound Long Form AI narrative listener and supplies both injectables. Neither default is
 * safe to leave in place: `resolveSessionId` defaults to identity, which would key narratives by
 * contactId and never match the panel's sessionId, and `isGuidedBrowsing` defaults to `false`, which
 * yields the wrong narrative shape (guide step dropped, enroll kept).
 *
 * Product info is read at event time via `getState()` rather than a selector — this hook mounts above
 * `SalesContextProvider`, so the session's product and language are not in React scope here.
 *
 * `getSelectedLanguage()` is app-wide, not per-session: with two calls open in different languages, a
 * narrative for the backgrounded one looks its product up in the on-screen language and can get the
 * wrong `guidedBrowsing` value. Bounded to that one boolean; per-session language is the real fix.
 */
export function useLongFormAiListenerWrapper() {
    const { features } = useFeatures<WorkspaceFeatures>();
    const guidedBrowsingEnabled = features.isGuidedBrowsingEnabled ?? false;

    useLongFormAiListener(
        (contactId) => getSessionByTaskId(contactId)?.id ?? contactId,
        (sku, sessionId) =>
            guidedBrowsingEnabled &&
            !!useProductOfferStore.getState().getProductInfo(sessionId, sku, getSelectedLanguage())?.guidedBrowsing,
    );
}
